import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { FileServerService } from '@app/core/services/file-server.service';
import { hashFile } from '@app/core/services/file-server.utils';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { Base64UrlService } from '@app/services/base64url.service';
import { DocArcService } from '@app/services/doc-arc.service';
import { DocumentItem, DocumentService } from '@app/services/document.service';
import { IdportenService } from '@app/services/idporten.service';
import { VatReportMenuGet, VatReportParams, VatReportService, VatReportSubmissionResponse, VatReportSubmissionStep } from '@app/services/vat-report.service';

@Component({
    selector: 'tt-vat-report-menu',
    templateUrl: './vat-report-menu.component.html',
    styleUrls: ['./vat-report-menu.component.css'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VatReportMenuComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: VatReportParams;

    @ViewChild('exportAnchor')
    private exportAnchor?: ElementRef<HTMLAnchorElement>;

    public page?: VatReportMenuGet;

    public grid?: GridOptions;

    public submissionSteps: VatReportSubmissionStep[] = [];

    public files: File[] = [];

    public attachments: DocumentItem[] = [];

    public receiptUrl = '';

    public loading = false;

    public action?: VatReportAction;

    public signInTooltip = '';

    private loadVersion = 0;

    constructor(
        private vatReport: VatReportService,
        private document: DocumentService,
        private docarc: DocArcService,
        private fileServer: FileServerService,
        public readonly idporten: IdportenService,
        private modal: ModalService,
        private appSettings: AppSettingsService,
        private translate: TranslateService,
        private base64: Base64UrlService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public get busy(): boolean {
        return this.loading || !!this.action;
    }

    public async login(): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'login';
        this.cdr.markForCheck();

        try {
            await this.idporten.login();
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async logout(): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'logout';
        this.cdr.markForCheck();

        try {
            await this.idporten.completeLogout();
            await this.idporten.logout();
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async updateReport(): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'update';
        this.cdr.markForCheck();

        try {
            const result = await this.vatReport.updateReport(this.reportParams());

            if (await this.checkResult(result)) {
                await this.reloadPage();
            }
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async accountReport(): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'account';
        this.cdr.markForCheck();

        try {
            const result = await this.vatReport.accountReport(this.reportParams());

            if (await this.checkResult(result)) {
                await this.reloadPage();
            }
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async deleteReport(): Promise<void> {
        if (!this.page || this.busy) return;

        const page = this.page;

        this.action = 'delete';
        this.cdr.markForCheck();

        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                translateTitle: true,
                message: 'confirm_message',
                translateMessage: true,
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label',
            });

            if (!confirmed || this.page !== page) return;

            const result = await this.vatReport.deleteReport(page);

            if ((await this.checkResult(result)) && this.page === page) {
                this.navigator?.back();
            }
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async validate(): Promise<void> {
        if (!this.page || this.busy) return;

        const page = this.page;
        const params = this.reportParams();

        this.action = 'validate';
        this.cdr.markForCheck();

        try {
            const token = await this.getAccessToken();

            if (!token || this.page !== page) return;

            const result = await this.vatReport.validateReport({
                ...params,
                id_porten_token: token,
            });

            if (this.page !== page) return;

            await this.showSubmissionResult(result);
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async sendToAltinn(transfer_only: 0 | 1): Promise<void> {
        if (!this.page || this.busy) return;

        const page = this.page;
        const params = this.reportParams();

        this.action = 'send';
        this.cdr.markForCheck();

        try {
            const token = await this.getAccessToken();

            if (!token || this.page !== page) return;

            const result = await this.vatReport.sendToAltinn({
                ...params,
                id_porten_token: token,
                transfer_only,
            });

            if (this.page !== page) return;

            await this.showSubmissionResult(result);
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async retrieveFeedback(): Promise<void> {
        if (!this.page || this.busy) return;

        const page = this.page;
        const params = this.reportParams();

        this.action = 'feedback';
        this.cdr.markForCheck();

        try {
            const token = await this.getAccessToken();

            if (!token || this.page !== page) return;

            const result = await this.vatReport.retrieveFeedback({
                ...params,
                id_porten_token: token,
            });

            if (this.page !== page) return;

            await this.showSubmissionResult(result);
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async exportToXml(): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'export';
        this.cdr.markForCheck();

        try {
            const result = await this.vatReport.exportReport(this.reportParams());

            if (!(await this.checkResult(result)) || !this.exportAnchor) return;

            const url = URL.createObjectURL(new Blob([this.base64.urlDecode(result.filecontent)], { type: 'text/txt' }));
            const anchor = this.exportAnchor.nativeElement;

            anchor.href = url;
            anchor.download = result.filename;
            anchor.click();

            // Let the browser start the download before releasing its temporary URL.
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    public async onFilesChanged(files: File[]): Promise<void> {
        if (!this.page || this.busy) return;

        this.action = 'attachments';
        this.cdr.markForCheck();

        try {
            const params = this.attachmentParams();
            const added = files.filter((file) => !this.files.some((existing) => existing.name === file.name));
            const removed = this.attachments.filter((item) => !files.some((file) => file.name === item.docfilename));

            try {
                for (const file of added) {
                    await this.uploadAttachment(file, params);
                }

                for (const item of removed) {
                    await this.fileServer.deleteFile({ fileName: item.docfilename });
                    await this.docarc.deleteDocArc({ docarc_keyno: item.item_id });
                }
            } finally {
                await this.loadAttachments();
            }
        } catch (error) {
            await this.showError(error);
        } finally {
            this.action = undefined;
            this.cdr.markForCheck();
        }
    }

    private async uploadAttachment(file: File, params: { argtype: string; argvalue: string }): Promise<void> {
        const checksum_md5 = await hashFile(file);

        const { exists } = await this.docarc.checkFileExists({
            filename: file.name,
            checksum_md5,
            argtype: params.argtype,
            argvalue: params.argvalue,
        });

        if (exists) return;

        const uploaded = await this.fileServer.uploadFile({ file });

        if ((uploaded.fileItemKeyno ?? 0) < 1) {
            throw Error(uploaded.message || `Could not upload ${file.name}`);
        }

        await this.docarc.add({
            filename: uploaded.fileName || '',
            localFileName: uploaded.localFileName || '',
            originalFileName: uploaded.originalFileName || '',
            originalTimestamp: uploaded.originalDateTime || '',
            contentType: uploaded.contentType || '',
            argType: params.argtype,
            argValue: params.argvalue,
            size: uploaded.size || 0,
            checksum: uploaded.checksum || '',
            checksum_md5,
            latitude: uploaded.latitude || 0,
            longitude: uploaded.longitude || 0,
            altitude: uploaded.altitude || 0,
            heading: uploaded.heading || 0,
        });
    }

    private async getAccessToken(): Promise<string | undefined> {
        const user = await this.idporten.getUser();

        if (user?.access_token && !user.expired) return user.access_token;

        await this.modal.show({
            type: 'warning',
            title: 'error',
            translateTitle: true,
            message: 'vatreportmenu_sign_in_idporten_first',
            translateMessage: true,
        });

        return undefined;
    }

    private async showSubmissionResult(result: VatReportSubmissionResponse): Promise<void> {
        if (this.page && result.submission_step_no !== undefined) {
            this.page.submission_step_no = result.submission_step_no;
        }

        if (this.page && result.receipt_base64 !== undefined) {
            this.page.receipt_base64 = result.receipt_base64;
            this.replaceReceiptUrl();
        }

        this.cdr.markForCheck();

        await this.modal.show({
            type: result.errorcode === '0' ? 'success' : 'danger',
            title: result.errorcode === '0' ? 'done' : 'error',
            translateTitle: true,
            message: result.errormessage,
        });
    }

    private async showError(error: unknown): Promise<void> {
        await this.modal.show({
            type: 'danger',
            title: 'error',
            translateTitle: true,
            message: error instanceof Error ? error.message : `${error}`,
        });
    }

    private async checkResult(result: ErrorMessageResponse): Promise<boolean> {
        if (result.errorcode === '0') return true;

        await this.modal.show({
            type: 'warning',
            title: 'error',
            translateTitle: true,
            message: result.errormessage,
        });

        return false;
    }

    private reportParams(): VatReportParams {
        return {
            vatreport_keyno: this.page!.vatreport_keyno,
        };
    }

    private attachmentParams() {
        return {
            argtype: 'vatreport_keyno',
            argvalue: this.page!.vatreport_keyno,
            doctype: 'a' as const,
            searchtext: '',
        };
    }

    private async loadAttachments(): Promise<void> {
        const version = this.loadVersion;
        const attachments = await this.document.getDocumentsList(this.attachmentParams());

        if (version !== this.loadVersion) return;

        this.attachments = attachments;
        this.files = attachments.map((item) => new File([], item.docfilename));
        this.cdr.markForCheck();
    }

    private replaceReceiptUrl(): void {
        if (this.receiptUrl) {
            URL.revokeObjectURL(this.receiptUrl);
        }

        this.receiptUrl = '';

        if (this.page?.receipt_base64) {
            const bytes = Uint8Array.from(atob(this.page.receipt_base64), (character) => character.charCodeAt(0));

            this.receiptUrl = URL.createObjectURL(new Blob([bytes.buffer], { type: 'application/pdf' }));
        }
    }

    private async reloadPage(): Promise<void> {
        this.page = await this.vatReport.getVatReportMenu(this.reportParams());
        this.replaceReceiptUrl();
        this.grid?.gridfunc?.read();
        this.cdr.markForCheck();
    }

    private async init(): Promise<void> {
        if (!this.params) return;

        const version = ++this.loadVersion;
        const params = this.params;

        this.loading = true;
        this.page = undefined;
        this.grid = undefined;
        this.attachments = [];
        this.files = [];
        this.replaceReceiptUrl();
        this.cdr.markForCheck();

        try {
            await this.appSettings.ensureIsReady();

            const [page, steps, title, tooltip] = await Promise.all([
                this.vatReport.getVatReportMenu(params),
                this.vatReport.getSubmissionSteps(),
                this.translate.translate('p2_m_vatreportmenu'),
                this.translate.translate('vatreportmenu_sign_in_idporten_first'),
            ]);

            if (version !== this.loadVersion) return;

            this.page = page;
            this.submissionSteps = steps.slice(1);
            this.signInTooltip = tooltip;
            this.navigator?.setHeading?.(title);
            this.replaceReceiptUrl();

            this.grid = new Grid()
                .setRememberId('w_vatreportmenu_vatreportdata_grid')
                .setLoadDataFunction({
                    method: 3136,
                    parameters: () => this.reportParams(),
                })
                .setKendoConfig({
                    height: null,
                    sortable: true,
                    aggregate: true,
                    pager: false,
                    selectable: false,
                    filterable: false,
                })
                .setSpecialFunc({ newTab: true })
                .setToolbar({
                    pdfExport: true,
                    excelExport: true,
                    filter: true,
                    headers: true,
                    wrapping: true,
                    layouts: true,
                    edit: false,
                    lock: false,
                    add: false,
                    delete: false,
                    save: false,
                    refresh: false,
                    read: false,
                    rebind: false,
                });

            await Promise.all([
                this.loadAttachments(),
                this.idporten.createUserManagerSettings(page.businessco_no),
            ]);
        } catch (error) {
            if (version === this.loadVersion) {
                await this.showError(error);
            }
        } finally {
            if (version === this.loadVersion) {
                this.loading = false;
                this.cdr.markForCheck();
            }
        }
    }

    ngOnInit(): void {
        if (!this.params && this.navigator) {
            this.params = {
                vatreport_keyno: String(this.navigator.params['vatreport_keyno'] ?? ''),
            };

            void this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            void this.init();
        }
    }

    ngOnDestroy(): void {
        ++this.loadVersion;

        if (this.receiptUrl) {
            URL.revokeObjectURL(this.receiptUrl);
        }
    }
}

type VatReportAction = 'login' | 'logout' | 'update' | 'account' | 'delete' | 'validate' | 'send' | 'feedback' | 'export' | 'attachments';
